import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { resolveAssetUrl } from "@/components/ui";
import { readableTextOn } from "@/lib/utils/color";
import { useBooth } from "../BoothContext";

const DEFAULT_WELCOME_BACKGROUND = "/brand/backgrounds/jebaxp-graphite-teal-welcome.png";
const DEFAULT_WORDMARK = "/brand/jebaxp-wordmark-light.png";
const DEMO_PHOTO = "/snapshot-filter-preview.jpg";

/** Premium attract screen. Camera access remains deferred until the guest starts. */
export function WelcomeScreen() {
  const booth = useBooth();
  const branding = booth.config.event.branding ?? {};
  const starting = useRef(false);
  const [storyScene, setStoryScene] = useState(0);
  const [pageVisible, setPageVisible] = useState(true);

  useEffect(() => { booth.playCue("welcome"); }, [booth]);

  useEffect(() => {
    const advance = () => setStoryScene((scene) => (scene + 1) % 4);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") advance();
    }, 4300);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const syncVisibility = () => setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", syncVisibility);
    return () => document.removeEventListener("visibilitychange", syncVisibility);
  }, []);

  const logo = branding.logo_url === undefined ? DEFAULT_WORDMARK : resolveAssetUrl(branding.logo_url);
  const configuredBackground = resolveAssetUrl(branding.background_url);
  const hasConfiguredBackground = branding.background_url !== undefined;
  const background = configuredBackground || (hasConfiguredBackground ? undefined : DEFAULT_WELCOME_BACKGROUND);
  const backgroundIsVideo = Boolean(background && (
    branding.background_media_type === "video"
    || /\.(mp4|webm|ogg)(?:$|\?)/i.test(background)
  ));
  const headline = branding.welcome_title === undefined ? "Your moment starts here." : branding.welcome_title;
  const subtitle = branding.welcome_subtitle === undefined ? "Pose. Capture. Make it yours." : branding.welcome_subtitle;
  const cta = branding.welcome_cta === undefined ? "Tap to start" : branding.welcome_cta;
  const accent = branding.primary_color ?? "#BEF264";
  const ctaText = branding.text_colors?.cta ?? readableTextOn(branding.secondary_color ?? "#090B09");
  const backgroundOpacity = hasConfiguredBackground ? branding.background_opacity ?? 1 : 1;
  const start = () => {
    if (starting.current) return;
    starting.current = true;
    booth.go(booth.firstStep);
  };

  return (
    <main onClick={start} className="welcome-screen relative flex h-full min-h-0 w-full overflow-hidden bg-[#090B09] text-[#F7F9F4]" data-custom-background={hasConfiguredBackground ? "true" : "false"} data-page-visible={pageVisible} style={{ "--welcome-accent": accent, "--welcome-cta-text": ctaText } as CSSProperties}>
      {backgroundIsVideo ? (
        <video
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          style={{ opacity: backgroundOpacity }}
        >
          <source src={background} />
        </video>
      ) : background ? (
        <img aria-hidden src={background} className="absolute inset-0 h-full w-full object-cover" style={{ opacity: backgroundOpacity }} alt="" />
      ) : (
        <div className="absolute inset-0 bg-[#090B09]" aria-hidden />
      )}
      <div className="welcome-screen-overlay absolute inset-0" aria-hidden />
      <div className="welcome-ambient-glow absolute left-[42%] top-[38%] h-[min(42vw,42rem)] w-[min(42vw,42rem)] -translate-x-1/2 -translate-y-1/2 rounded-full" aria-hidden />
      <div className="relative z-10 grid min-h-0 w-full flex-1 grid-rows-[auto_auto_minmax(0,1fr)_auto] px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1.5rem,env(safe-area-inset-top))] sm:px-10 lg:px-16">
        <header className="flex shrink-0 items-center justify-between">
          {logo && <img src={logo} alt="JebaXP" className="welcome-logo h-auto w-[clamp(8.5rem,22vw,15rem)] object-contain object-left" />}
          <span className="rounded-full border border-[var(--welcome-accent)]/40 bg-black/25 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--welcome-accent)] backdrop-blur-sm sm:text-xs">Photo booth</span>
        </header>

        <section className="welcome-hero-copy flex min-h-0 flex-col justify-center pb-3 pt-8 sm:pb-4 sm:pt-10 lg:max-w-3xl">
          <p className="welcome-copy-line mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-[var(--welcome-accent)] sm:text-sm">JebaXP experience</p>
          <h1 className="welcome-copy-line max-w-3xl text-[clamp(2.5rem,7vw,5.75rem)] font-bold leading-[0.96] tracking-[-0.045em] text-white">{headline}</h1>
          {subtitle && <p className="welcome-copy-line mt-5 max-w-xl text-[clamp(1rem,2vw,1.45rem)] leading-relaxed text-white/75">{subtitle}</p>}
        </section>

        <section className="relative min-h-0 overflow-hidden" aria-label="JebaXP photo booth preview">
          <AttractStory scene={storyScene} accent={accent} qrEnabled={booth.config.event.qr_code?.enabled !== false} subdued={hasConfiguredBackground} showCopy={branding.welcome_title === undefined && branding.welcome_subtitle === undefined} />
        </section>

        <div className="welcome-actions flex min-h-24 items-center justify-center pt-3">
          <button type="button" onClick={(event) => { event.stopPropagation(); start(); }} disabled={starting.current} className="welcome-start pointer-events-auto inline-flex min-h-16 w-[88%] max-w-[34rem] items-center justify-center rounded-2xl bg-[var(--welcome-accent)] px-8 py-4 text-lg font-bold text-[var(--welcome-cta-text)] shadow-[0_12px_40px_rgba(190,242,100,.22)] transition-transform active:scale-[.98] disabled:cursor-wait disabled:opacity-80 sm:min-h-[4.75rem] sm:px-12 sm:text-2xl">
            {cta} <span aria-hidden className="welcome-arrow ml-3 text-2xl">→</span>
          </button>
        </div>
      </div>
    </main>
  );
}

function AttractStory({ scene, accent, qrEnabled, subdued, showCopy }: { scene: number; accent: string; qrEnabled: boolean; subdued: boolean; showCopy: boolean }) {
  const reduceMotion = useReducedMotion();
  const stories = [
    { title: "Capture the moment", copy: "Pose. Capture. Make it yours." },
    { title: "One moment. Endless memories.", copy: "Choose your style and make every shot count." },
    { title: "Add your own magic.", copy: "Personalize every memory." },
    { title: "Capture. Create. Keep.", copy: "Your photo, your memory." },
  ];
  const story = stories[scene]!;
  return <div className={`welcome-story pointer-events-none absolute inset-0 flex items-center justify-center p-2 sm:p-4 ${subdued ? "opacity-40" : ""}`} aria-hidden>
    <motion.div key={scene} initial={reduceMotion ? false : { opacity: 0, y: 18, scale: .96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={reduceMotion ? { duration: 0 } : { duration: .55, ease: "easeOut" }} className="flex h-full min-h-0 w-full max-w-[34rem] flex-col items-center gap-2 text-center sm:gap-3">
      <div className="welcome-story-visual relative min-h-0 w-full flex-1 overflow-hidden">
        {scene === 0 && <PhotoCard className="welcome-card-one" />}
        {scene === 1 && <LayoutShowcase />}
        {scene === 2 && <EditedPhoto />}
        {scene === 3 && <PhoneShowcase showQr={qrEnabled} />}
      </div>
      {showCopy && <div>
        <h2 className="text-xl font-bold text-white sm:text-3xl" style={{ color: accent }}>{story.title}</h2>
        <p className="mt-2 text-sm text-white/70 sm:text-base">{story.copy}</p>
      </div>}
    </motion.div>
  </div>;
}

function PhotoCard({ className = "" }: { className?: string }) {
  return <div className={`welcome-demo-card absolute left-1/2 top-1/2 h-[72%] w-[34%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border-4 border-white/80 shadow-2xl ${className}`}><img src={DEMO_PHOTO} alt="" className="h-full w-full object-cover" /></div>;
}

function LayoutShowcase() {
  return <div className="absolute inset-0 flex items-center justify-center gap-3">
    <PhotoCard className="!static !m-0 !h-[64%] !w-[23%] !translate-x-0 !translate-y-0" />
    <div className="flex h-[78%] w-[30%] flex-col gap-2 rounded-xl border-2 border-white/70 bg-black/25 p-2 shadow-2xl"><img src={DEMO_PHOTO} alt="" className="h-1/2 w-full rounded object-cover" /><img src={DEMO_PHOTO} alt="" className="h-1/2 w-full rounded object-cover" /></div>
    <div className="grid h-[62%] w-[30%] grid-cols-2 gap-2 rounded-xl border-2 border-white/70 bg-black/25 p-2 shadow-2xl">{Array.from({ length: 4 }, (_, i) => <img key={i} src={DEMO_PHOTO} alt="" className="h-full w-full rounded object-cover" />)}</div>
  </div>;
}

function EditedPhoto() {
  return <div className="absolute left-1/2 top-1/2 h-[72%] w-[38%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border-4 border-white/80 shadow-2xl"><img src={DEMO_PHOTO} alt="" className="h-full w-full object-cover" /><span className="absolute bottom-7 left-1/2 -translate-x-1/2 rounded-full bg-black/65 px-4 py-2 text-lg font-semibold text-white">Best day ever ✨</span><span className="absolute right-3 top-3 text-3xl">❤️</span></div>;
}

function PhoneShowcase({ showQr }: { showQr: boolean }) {
  return <div className="absolute inset-0 flex items-center justify-center gap-5"><div className="welcome-phone h-[78%] w-[34%] overflow-hidden rounded-[2rem] border-4 border-white/70 bg-[#131713] p-2 shadow-2xl"><div className="h-full overflow-hidden rounded-[1.5rem] bg-black"><img src={DEMO_PHOTO} alt="" className="h-[72%] w-full object-cover" />{showQr && <div className="mx-auto mt-3 grid h-14 w-14 grid-cols-4 gap-1 bg-white p-1">{Array.from({ length: 16 }, (_, i) => <span key={i} className={i % 3 ? "bg-black" : "bg-white"} />)}</div>}</div></div><div className="text-5xl text-white/80">↗</div></div>;
}
